import ActionTypes from './actionTypes';
import { switchExpiryDate } from '../utilities/card-utilities';

/**
 * Get cards by account number
 */
export function getCardsRequest(payload: Record<string, any>, isFilterClick = false) {
  payload.sortDirection = payload.sortDirection ? 'ASC' : 'DESC';
  return {
    type: ActionTypes.CARDS.REQUEST,
    payload: {
      ...payload,
      isFilterClick,
    },
  };
}

export function getCardsSuccess(response: any) {
  return {
    type: ActionTypes.CARDS.SUCCESS,
    payload: response,
  };
}

export function getCardsFailure() {
  return {
    type: ActionTypes.CARDS.FAILURE,
  };
}

export function getCardsReset() {
  return {
    type: ActionTypes.CARDS.RESET,
  };
}

/**
 * Get card details
 */
export function getCardDetailRequest(
  cardNumber: string,
  grandParentAccountNumber: string,
  isPreviousOrReplacementCardClicked = false
) {
  const requestPayload = {
    cardNumber,
    grandParentAccountNumber,
    isPreviousOrReplacementCardClicked,
  };

  return {
    type: ActionTypes.CARDS.DETAIL.REQUEST,
    payload: requestPayload,
  };
}

export function getCardDetailSuccess(response: any) {
  return {
    type: ActionTypes.CARDS.DETAIL.SUCCESS,
    payload: {
      details: response,
    },
  };
}

export function resetCardDetails() {
  return {
    type: ActionTypes.CARDS.DETAIL.RESET,
  };
}

export function getCardDetailFailure() {
  return {
    type: ActionTypes.CARDS.DETAIL.FAILURE,
  };
}

export function transferSingleCard(
  cardNumber: string,
  sourceAccountNumber: string,
  targetAccountNumber: string,
  effectiveDate: string,
  grandParentAccountNumber: string,
  targetGrandParentAccountNumber: string
) {
  const transferCardRequest = JSON.stringify({
    sourceIdentifiers: [
      {
        sourceContractSearchMethod: 'CONTRACT_NUMBER',
        sourceContractIdentifier: sourceAccountNumber,
        cardsList: cardNumber,
        moveAllCards: 'N',
      },
    ],
    targetContractSearchMethod: 'CONTRACT_NUMBER',
    targetContractIdentifier: targetAccountNumber,
    effectiveDate,
    newProductCode: 'ISSCRDACARD',
    grandParentAccountNumber,
    targetGrandParentAccountNumber,
  });

  return {
    type: ActionTypes.CARDS.TRANSFER_SINGLE.REQUEST,
    payload: transferCardRequest,
  };
}

export function transferBatchCards(
  cardsList: Record<string, any>,
  targetAccountNumber: string,
  effectiveDate: string,
  grandParentAccountNumber: string,
  targetGrandParentAccountNumber: string
) {
  const batchList = Object.values(cardsList ?? {})
    .filter((x: any) => x?.isSelected)
    .reduce((acc: Record<string, any[]>, curr: any) => {
      const { accountNumber } = curr;
      if (!acc[accountNumber]) acc[accountNumber] = [];
      acc[accountNumber].push(curr);
      return acc;
    }, {});

  const transferCardRequest = JSON.stringify({
    sourceIdentifiers: Object.entries(batchList).map(([key, cards]) => ({
      sourceContractSearchMethod: 'CONTRACT_NUMBER',
      sourceContractIdentifier: key,
      cardsList: (cards as any[]).map((card) => card.cardNumber).join(';'),
      moveAllCards: 'N',
    })),
    targetContractSearchMethod: 'CONTRACT_NUMBER',
    targetContractIdentifier: targetAccountNumber,
    effectiveDate,
    newProductCode: 'ISSCRDACARD',
    grandParentAccountNumber,
    targetGrandParentAccountNumber,
  });

  return {
    type: ActionTypes.CARDS.TRANSFER_BATCH.REQUEST,
    payload: transferCardRequest,
  };
}

export function transferSingleCardsReset() {
  return {
    type: ActionTypes.CARDS.TRANSFER_SINGLE.RESET,
  };
}

export function transferBatchCardsReset() {
  return {
    type: ActionTypes.CARDS.TRANSFER_BATCH.RESET,
  };
}

export function activateCard(cardNumber: string, userId: string, grandParentAccountNumber: string) {
  const activateCardRequest = JSON.stringify({
    contractSearchMethod: 'CONTRACT_NUMBER',
    userId,
    grandParentAccountNumber,
  });

  return {
    type: ActionTypes.CARDS.ACTIVATE.REQUEST,
    payload: {
      cardNumber,
      activateCardRequest,
    },
  };
}

export function activateMultipleCards(cardsList: string, userId: string, grandParentAccountNumber: string) {
  const activateCardsRequest = JSON.stringify({
    CardNumberList: cardsList,
    userId,
    grandParentAccountNumber,
  });

  return {
    type: ActionTypes.CARDS.ACTIVATE_MULTIPLE.REQUEST,
    payload: activateCardsRequest,
  };
}

export function resetActivateCard() {
  return {
    type: ActionTypes.CARDS.ACTIVATE.RESET,
  };
}

export function resetActivateMultipleCards() {
  return {
    type: ActionTypes.CARDS.ACTIVATE_MULTIPLE.RESET,
  };
}

export function setCardStatus(
  cardNumber: string,
  status: string,
  grandParentAccountNumber: string,
  reissueCard = false
) {
  const setCardStatusRequest = JSON.stringify({
    contractSearchMethod: 'CONTRACT_NUMBER',
    contractIdentifier: cardNumber,
    newStatus: status,
    grandParentAccountNumber,
    reissueCard,
  });

  return {
    type: ActionTypes.CARDS.STATUS.REQUEST,
    payload: {
      setCardStatusRequest,
    },
  };
}

export function resetSingleCardStatus() {
  return {
    type: ActionTypes.CARDS.STATUS.RESET,
  };
}

export function setMultipleCardStatus(
  cardList: string,
  status: string,
  grandParentAccountNumber: string,
  reissueCard = false
) {
  const setMultipleCardStatusRequest = JSON.stringify({
    cardList,
    newStatus: status,
    grandParentAccountNumber,
    reissueCard,
  });

  return {
    type: ActionTypes.CARDS.MULTIPLE_CARDS_STATUS.REQUEST,
    payload: {
      setMultipleCardStatusRequest,
    },
  };
}

export function resetMultipleCardStatus() {
  return {
    type: ActionTypes.CARDS.MULTIPLE_CARDS_STATUS.RESET,
  };
}

export function setCardOptions(updateMultipleCardRequest: any, grandfatherClientNumber: string) {
  return {
    type: ActionTypes.CARDS.CARD_OPTIONS.REQUEST,
    payload: {
      updateMultipleCardRequest,
      grandfatherClientNumber,
    },
  };
}

export function resetCardOptions() {
  return {
    type: ActionTypes.CARDS.CARD_OPTIONS.RESET,
  };
}

export function getPendingTransferCardsReset() {
  return {
    type: ActionTypes.CARDS.PENDING.RESET,
  };
}

export function getPendingTransferCards(
  pageSize: number,
  pageNumber: number,
  sortColumn: string,
  sortOrdering: boolean,
  searchTerm: string,
  contractIdentifier: string,
  grandParentAccountNumber: string
) {
  const sortDirection = !sortOrdering ? 'DESC' : 'ASC';

  return {
    type: ActionTypes.CARDS.PENDING.REQUEST,
    payload: {
      pageSize,
      pageNumber,
      sortColumn,
      sortDirection,
      searchTerm,
      contractSearchMethod: 'CONTRACT_NUMBER',
      contractIdentifier,
      grandParentAccountNumber,
    },
  };
}

export function cardsDownloadRequest(payload: Record<string, any>) {
  payload.sortDirection = payload.sortDirection ? 'ASC' : 'DESC';
  return {
    type: ActionTypes.CARDS.DOWNLOAD.REQUEST,
    payload,
  };
}

export function cardsDownloadFailure() {
  return {
    type: ActionTypes.CARDS.DOWNLOAD.FAILURE,
  };
}

export function cardsDownloadSuccess() {
  return {
    type: ActionTypes.CARDS.DOWNLOAD.SUCCESS,
  };
}

export function cardsUpdatePinRequest(
  cardIds: string[],
  newPin: string,
  userId: string,
  grandParentAccountNumber: string
) {
  return {
    type: ActionTypes.CARDS.UPDATE_PIN.REQUEST,
    payload: {
      cardIds,
      newPin,
      userId,
      grandParentAccountNumber,
    },
  };
}

export function cardsUpdatePinSuccess() {
  return {
    type: ActionTypes.CARDS.UPDATE_PIN.SUCCESS,
  };
}

export function cardsUpdatePinFailure() {
  return {
    type: ActionTypes.CARDS.UPDATE_PIN.FAILURE,
  };
}

export function cardsResetUpdatePin() {
  return {
    type: ActionTypes.CARDS.UPDATE_PIN.RESET,
  };
}

export function updateCardsSearchTerm(searchTerm: string) {
  return {
    type: ActionTypes.CARDS.UPDATE.SEARCH_TERM,
    payload: {
      searchTerm,
    },
  };
}

export function createCard(cardDetails: Record<string, any>) {
  const createCardRequest = { ...cardDetails };
  // convert expiry date to server format YYMM if it is MM/YY
  if (createCardRequest.expiryDate?.includes('/')) {
    createCardRequest.expiryDate = switchExpiryDate(createCardRequest.expiryDate);
  }

  return {
    type: ActionTypes.CARDS.CREATE.REQUEST,
    payload: {
      createCardRequest,
    },
  };
}

export function resetCreateCard() {
  return {
    type: ActionTypes.CARDS.CREATE.RESET,
  };
}

export function editCardDetails(updateCardRequest: Record<string, any>, grandfatherClientNumber: string) {
  // convert expiry date to server format YYMM if it is MM/YY
  if (updateCardRequest.expiryDate?.includes('/')) {
    updateCardRequest.expiryDate = switchExpiryDate(updateCardRequest.expiryDate);
  }

  return {
    type: ActionTypes.CARDS.EDIT.REQUEST,
    payload: {
      updateCardRequest,
      grandfatherClientNumber,
    },
  };
}

export function resetEditCard() {
  return {
    type: ActionTypes.CARDS.EDIT.RESET,
  };
}

export function editMultipleCardDetails(updateMultipleCardRequest: any, grandfatherClientNumber: string) {
  return {
    type: ActionTypes.CARDS.UPDATE_MULTIPLE.REQUEST,
    payload: {
      updateMultipleCardRequest,
      grandfatherClientNumber,
    },
  };
}

export function resetUpdateMultipleCardDetails() {
  return {
    type: ActionTypes.CARDS.UPDATE_MULTIPLE.RESET,
  };
}

export function getCardAccountDefaultRequest(payload: any) {
  return {
    type: ActionTypes.CARDS.ACCOUNT_DEFAULT.REQUEST,
    payload,
  };
}

export function resetCardAccountDefault() {
  return {
    type: ActionTypes.CARDS.ACCOUNT_DEFAULT_RESET.REQUEST,
  };
}

export function cardsResetPinRequest(cardIds: string[], parentAccountNumber: string, grandParentAccountNumber: string) {
  return {
    type: ActionTypes.CARDS.RESET_PIN.REQUEST,
    payload: {
      cardIds,
      parentAccountNumber,
      grandParentAccountNumber,
    },
  };
}

export function cardsResetPinSuccess() {
  return {
    type: ActionTypes.CARDS.RESET_PIN.SUCCESS,
  };
}

export function cardsResetPinReset() {
  return {
    type: ActionTypes.CARDS.RESET_PIN.RESET,
  };
}

export function cardsResetPinFailure() {
  return {
    type: ActionTypes.CARDS.RESET_PIN.FAILURE,
  };
}

export function cancelPendingTransferBatchRequest(
  transferTargets: Record<string, string[]>,
  grandParentAccountNumber: string
) {
  return {
    type: ActionTypes.CARDS.CANCEL_PENDING_TRANSFER_BATCH.REQUEST,
    payload: {
      transferTargets,
      grandParentAccountNumber,
    },
  };
}

export function cancelPendingTransferBatchSuccess() {
  return {
    type: ActionTypes.CARDS.CANCEL_PENDING_TRANSFER_BATCH.SUCCESS,
  };
}

export function cancelPendingTransferBatchFailure() {
  return {
    type: ActionTypes.CARDS.CANCEL_PENDING_TRANSFER_BATCH.FAILURE,
  };
}

export function updatePendingTransferBatchRequest(
  targetContractIdentifier: string,
  newEffectiveDate: string,
  transferSources: Record<string, string[]>,
  grandParentAccountNumber: string,
  targetGrandParentAccountNumber: string
) {
  return {
    type: ActionTypes.CARDS.UPDATE_PENDING_TRANSFER_BATCH.REQUEST,
    payload: {
      targetContractIdentifier,
      newEffectiveDate,
      transferSources,
      grandParentAccountNumber,
      targetGrandParentAccountNumber,
    },
  };
}

export function updatePendingTransferBatchSuccess() {
  return {
    type: ActionTypes.CARDS.UPDATE_PENDING_TRANSFER_BATCH.SUCCESS,
  };
}

export function updatePendingTransferBatchFailure() {
  return {
    type: ActionTypes.CARDS.UPDATE_PENDING_TRANSFER_BATCH.FAILURE,
  };
}

export function pendingTransfersDownloadRequest(
  grandParentAccountNumber: string,
  contractIdentifier: string,
  limit: number,
  offset: number,
  sortBy: string,
  sortOrder: string,
  searchTerm: string
) {
  return {
    type: ActionTypes.CARDS.PENDING_TRANSFERS_DOWNLOAD.REQUEST,
    payload: {
      grandParentAccountNumber,
      contractIdentifier,
      limit,
      offset,
      sortBy,
      sortOrder,
      searchTerm,
      contractSearchMethod: 'CONTRACT_NUMBER',
    },
  };
}

export function pendingTransfersDownloadFailure() {
  return {
    type: ActionTypes.CARDS.PENDING_TRANSFERS_DOWNLOAD.FAILURE,
  };
}

export function pendingTransfersDownloadSuccess() {
  return {
    type: ActionTypes.CARDS.PENDING_TRANSFERS_DOWNLOAD.SUCCESS,
  };
}

export function updateCardModalFormValues(values: any) {
  return {
    type: ActionTypes.CARDS.UPDATE_CARD_MODAL_FORM_VALUES,
    payload: {
      formValues: values,
    },
  };
}

export function globalSearchRequest(searchTerm: string, navigate: any) {
  return {
    type: ActionTypes.CARDS.GLOBAL_SEARCH.REQUEST,
    payload: {
      searchTerm,
      navigate,
    },
  };
}

export function globalSearchReset() {
  return {
    type: ActionTypes.CARDS.GLOBAL_SEARCH.RESET,
  };
}

export function updateSelectedCard(selectedCard: any) {
  return {
    type: ActionTypes.CARDS.UPDATE_SELECTED_CARD,
    payload: {
      selectedCard,
    },
  };
}

export function updateCardModalMeta(modalMeta: any) {
  return {
    type: ActionTypes.CARDS.UPDATE_CARD_MODAL_META,
    payload: {
      modalMeta,
    },
  };
}

export function showCardCreatedModal(cardNumber: string) {
  return {
    type: ActionTypes.CARDS.CREATE.SHOW_MODAL,
    payload: {
      cardNumber,
    },
  };
}

export function hideCardCreatedModal() {
  return {
    type: ActionTypes.CARDS.CREATE.HIDE_MODAL,
  };
}

export function resetAddNewCard() {
  return {
    type: ActionTypes.CARDS.CREATE.RESET_ADD_CARD,
  };
}

export function disableExpiryDate(value: boolean) {
  return {
    type: ActionTypes.CARDS.DISABLE_EXPIRY_DATE,
    payload: {
      disableExpiryDate: value,
    },
  };
}
